import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, of, retry, switchMap } from 'rxjs';

import { AccountPermissionsService } from '../../services/account-permissions/account-permissions.service';
import {
    accountUsersDataLoadedError,
    accountUsersDataLoadedSuccess,
    clientSegmentContentLoadedError,
    clientSegmentContentLoadedRedirect,
    clientSegmentContentLoadedSuccess,
} from '../actions/account-permissions-api.action';
import * as AccountPermissionPageActions from '../actions/account-permissions-page.action';
import { LoggerService } from '../../services/logger/logger.service';
import {
    ClientSegmentContentDTO,
    ClientSegmentRedirectInstruction,
} from 'src/common/dtos/client-segment.dto';
import { content } from '../../../content/zci/content'
import { loadDefaultClientSegmentContent } from '../actions/account-permissions-page.action';

@Injectable({
    providedIn: 'root',
})
export class AccountPermissionsApiEffect {
    private readonly accountPermissionsService = inject(AccountPermissionsService);
    private readonly actions$ = inject(Actions);
    private readonly logger = inject(LoggerService);


    loadAccountUsers$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AccountPermissionPageActions.loadAccountUsers),
            switchMap(() => {
                return this.accountPermissionsService.getAccountUsers().pipe(
                    retry(2),
                    map((result) => {
                        this.logger.info('Accounts successfully loaded');
                        return accountUsersDataLoadedSuccess({
                            data: result,
                        });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        this.logger.error('Accounts failed to load');
                        return of(accountUsersDataLoadedError({ error }));
                    }),
                );
            }),
        );
    });

    loadClientSegmentContent$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AccountPermissionPageActions.loadClientSegmentContent),
            switchMap(() => {
                return this.accountPermissionsService.getClientSegmentContent().pipe(
                    retry(2),
                    map((res) => {
                        const { redirectUrl } = res as ClientSegmentRedirectInstruction;
                        if (redirectUrl) {
                            const safeUrl: URL = new URL(redirectUrl);
                            const trustedHost: string = 'vanguard.com';

                            if (safeUrl.hostname.endsWith(trustedHost)) {
                                window.location.href = redirectUrl;
                                return clientSegmentContentLoadedRedirect({
                                    error: { status: 302 } as HttpErrorResponse,
                                });
                            }
                        }

                        const clientSegmentContentData = res as ClientSegmentContentDTO;

                        this.logger.info('Client Segment content successfully loaded');
                        return clientSegmentContentLoadedSuccess({
                            data: clientSegmentContentData,
                        });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        this.logger.error('Client Segment content failed to load');
                        return of(
                            clientSegmentContentLoadedError({ error }),
                            loadDefaultClientSegmentContent({ data: content.defaultClientSegmentContent }),
                        );
                    }),
                );
            }),
        );
    });
}
